"use client";

import { invalidateMatterAndSearchQueries } from "@/lib/data/react-query/search/invalidation";

import { FileReferencesSection } from "@/components/features/matter/file-repo/FileReferencesSection";
import { SelectTreatmentAppointmentsFormField } from "@/components/features/matter/medical-treatments/SelectTreatmentAppointmentsFormField";
import { TrustCheckCard } from "@/components/features/matter/trust-checks/TrustCheckCard";
import {
  ActionConfirmationModal,
  AppModal,
  Button,
  CheckboxFormField,
  CurrencyWrapper,
  DateFormField,
  Form,
  FormSubmitButton,
  Separator,
  SimpleSelectFormField,
  TextAreaFormField,
  TextFormField,
  Typography,
} from "@/components/ui";
import { FormLoadingOverlay } from "@/components/ui/Form/FormLoadingOverlay";
import { CurrencyFormField } from "@/components/ui/FormFields/CurrencyFormField/CurrencyFormField";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { useUser } from "@/components/UserProvider";
import { useMedicalTreatmentBillViewModel } from "@/hooks/matter-view-models/use-medical-treatment-bill-view-model";
import { useMedicalTreatmentViewModel } from "@/hooks/matter-view-models/use-medical-treatment-view-model";
import {
  useCreateMedicalTreatmentAppointmentConnection,
  useDeleteMedicalTreatmentAppointmentConnection,
} from "@/lib/data/react-query";
import { retrieveMatterQueryOptions } from "@/lib/data/react-query/matters/matter";
import {
  useCreateMedicalTreatmentBill,
  useDeleteMedicalTreatmentBill,
  useUpdateMedicalTreatmentBill,
} from "@/lib/data/react-query/matters/medical-treatment-bill";
import { MedicalTreatmentBillSchema } from "@/lib/data/schemas";
import {
  MEDICAL_TREATMENT_LETTER_OF_PROTECTION_STATUS_OPTIONS,
  MEDICAL_TREATMENT_LIEN_STATUS_OPTIONS,
  MEDICAL_TREATMENT_RECORD_SELECTABLE_STATUS_OPTIONS,
} from "@/lib/data/static/matterStatics";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import {
  SLIDEOVER_DESCRIPTION,
  SLIDEOVER_TITLE,
} from "@/lib/data/static/slideoverStatics";
import {
  type APIResponse,
  type MatterSlideoverInitialTabsType,
  type MedicalTreatmentBill,
} from "@/lib/types";
import { mergeInitialData } from "@/lib/utils/initialDataUtils";
import { mergeFilesAndReferenceId } from "@/lib/utils/matterFile";
import {
  calculateMedicalTreatmentBillTotals,
  canSetMedicalTreatmentBillLien,
} from "@/lib/utils/matters/medical-treatment-bill-utils";
import { modalFormFieldsetClassName } from "@/lib/utils/modal-classes";
import { useModals } from "@/providers/ModalProvider";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { difference, isNil, omitBy } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { type SubmitErrorHandler, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { z } from "zod";

interface AddOrEditTreatmentBillSlideoverProps {
  title: string;
  description?: string;
  open: boolean;
  setOpen: (open: boolean) => void;
  editing?: boolean;
  initialTab?: MatterSlideoverInitialTabsType;
  matterId: string;
  billId?: string;
  treatmentId?: string;
  afterCreateFn?: (newBill: APIResponse<MedicalTreatmentBill>) => void;
  creatingFromAppointment?: boolean;
  initialData?: Partial<MedicalTreatmentBill>;
}

const billFormSchema = MedicalTreatmentBillSchema.omit({
  connections: true,
  connectionsNextCursor: true,
  id: true,
  dateCreated: true,
  dateModified: true,
  matterId: true,
  medicalTreatmentId: true,
})
  .extend({
    recordStatus: z
      .enum(MEDICAL_TREATMENT_RECORD_SELECTABLE_STATUS_OPTIONS)
      .or(z.literal(""))
      .refine((value) => value, {
        message: "Status is required",
      }),
    medicalTreatmentAppointmentId: z.array(z.string()).optional(),
  })
  .superRefine((data, ctx) => {
    if (["Requested", "Received"].includes(data.recordStatus)) {
      if (!data.recordRequestDate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Bill request date is required",
          path: ["recordRequestDate"],
        });
      }
    }
    if (data.recordStatus === "Received") {
      if (!data.recordReceivedDate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Record received date is required",
          path: ["recordReceivedDate"],
        });
      }
    }
    if (data.recordStatus === "Rejected") {
      if (!data.recordRejectedDate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Record rejected date is required",
          path: ["recordRejectedDate"],
        });
      }
      if (!data.recordRejectedReason?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Rejected reason is required",
          path: ["recordRejectedReason"],
        });
      }
    }
  });

const defaultFormValues = {
  description: "",
  totalPaid: undefined,
  totalPaidPlusOwed: undefined,
  remainingBalance: undefined,
  adjustedBill: undefined,
  clientPaid: undefined,
  insurancePaid: undefined,
  medpayPaid: undefined,
  originalBill: null,
  medicalTreatmentBillsPIPPaid: undefined,
  recordRequestDate: "",
  recordReceivedDate: undefined,
  recordRejectedDate: undefined,
  recordRejectedReason: undefined,
  recordStatus: "Requested" as const,
  recordLabel: "",
  fileNumber: null,
  letterOfProtection: undefined,
  lien: undefined,
  goalAmount: null,
  negotiatedAmount: null,
  reductionFeeAgreement: false,
  medicalTreatmentAppointmentId: [],
};

const receivedOnlyBillFields = {
  originalBill: null,
  goalAmount: null,
  adjustedBill: undefined,
  negotiatedAmount: null,
  clientPaid: undefined,
  insurancePaid: undefined,
  medpayPipPaid: undefined,
};

type SelectableTreatmentRecordStatus =
  (typeof MEDICAL_TREATMENT_RECORD_SELECTABLE_STATUS_OPTIONS)[number];

const getSelectableTreatmentRecordStatus = (
  status?: string | null,
): SelectableTreatmentRecordStatus | "" =>
  status &&
  MEDICAL_TREATMENT_RECORD_SELECTABLE_STATUS_OPTIONS.includes(
    status as SelectableTreatmentRecordStatus,
  )
    ? (status as SelectableTreatmentRecordStatus)
    : "";

const normalizeBillFormValuesForStatus = <
  T extends Partial<MedicalTreatmentBill>,
>(
  values: T,
) => {
  const normalizedValues = {
    ...values,
    aiAffectedFields: values.aiAffectedFields ?? [],
    aiStatus: values.aiStatus ?? undefined,
    description: values.description ?? "",
    letterOfProtection: values.letterOfProtection ?? "",
    lien: values.lien ?? "",
    reductionFeeAgreement: values.reductionFeeAgreement ?? false,
    status: values.status ?? undefined,
  };

  if (values.recordStatus === "Received") {
    return normalizedValues;
  }

  return {
    ...normalizedValues,
    ...receivedOnlyBillFields,
    recordReceivedDate: undefined,
  };
};

export const AddOrEditTreatmentBillSlideover = ({
  title,
  description,
  open,
  setOpen,
  editing,
  matterId,
  billId,
  treatmentId,
  afterCreateFn,
  creatingFromAppointment,
  initialData,
}: AddOrEditTreatmentBillSlideoverProps) => {
  const { hasActionPermission } = useUser();
  const canCreateAppointmentConnections = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterMedicalTreatmentAppointmentConnection,
    ACTION_PERMISSION_VALUES.create,
  );
  const canDeleteAppointmentConnections = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterMedicalTreatmentAppointmentConnection,
    ACTION_PERMISSION_VALUES.delete,
  );
  const { updateSlideoverPayload } = useModals();
  const queryClient = useQueryClient();

  const createBill = useCreateMedicalTreatmentBill({
    suppressSuccessToast: true,
    suppressQueryInvalidation: true,
  });
  const updateBill = useUpdateMedicalTreatmentBill({
    suppressSuccessToast: true,
    suppressQueryInvalidation: true,
  });
  const deleteBill = useDeleteMedicalTreatmentBill();
  const createMedicalTreatmentAppointmentConnection =
    useCreateMedicalTreatmentAppointmentConnection({
      suppressQueryInvalidation: true,
    });
  const deleteMedicalTreatmentAppointmentConnection =
    useDeleteMedicalTreatmentAppointmentConnection({
      suppressQueryInvalidation: true,
    });

  const isFormLoading =
    createBill.isPending ||
    updateBill.isPending ||
    createMedicalTreatmentAppointmentConnection.isPending ||
    deleteMedicalTreatmentAppointmentConnection.isPending;

  const [isDeleting, setIsDeleting] = useState(false);

  // Fetch matter data
  const { data: matterData } = useSuspenseQuery(
    retrieveMatterQueryOptions(matterId),
  );

  const matter = matterData.data;
  const medicalTreatmentBillsViewModel =
    useMedicalTreatmentBillViewModel(matterId);

  // Get the specific bill if editing
  const selectedBill = billId
    ? medicalTreatmentBillsViewModel.find((bill) => bill.id === billId)
    : undefined;
  const billInitialValues = mergeInitialData(selectedBill, initialData);

  // Get the specific treatment
  const selectedTreatment = treatmentId
    ? matter.medicalTreatments.find((treatment) => treatment.id === treatmentId)
    : undefined;

  const completeMedicalTreatments = useMedicalTreatmentViewModel(matter);
  const completeTreatment = completeMedicalTreatments.find(
    (t) => t.id === selectedTreatment?.id,
  );
  const appointmentsWithConnections = completeTreatment?.appointments;
  const appointmentsWIthSelectedBill = appointmentsWithConnections?.filter(
    (a) => a.bills.some((b) => b.id === billInitialValues?.id),
  );

  const fileReferences = mergeFilesAndReferenceId(
    matter,
    "medicalTreatmentBillId",
    billInitialValues?.id,
  );

  const editFormValues = useMemo(
    () => ({
      ...defaultFormValues,
      ...normalizeBillFormValuesForStatus(billInitialValues ?? {}),
      recordStatus: getSelectableTreatmentRecordStatus(
        billInitialValues?.recordStatus,
      ),
      recordLabel: billInitialValues?.recordLabel ?? "",
      medicalTreatmentAppointmentId:
        appointmentsWIthSelectedBill?.map((appointment) => appointment.id) ??
        [],
    }),
    [billInitialValues, appointmentsWIthSelectedBill],
  );

  const newBillFormValues = useMemo(
    () => ({
      ...defaultFormValues,
      ...(selectedTreatment?.letterOfProtection
        ? {
            letterOfProtection: selectedTreatment.letterOfProtection,
          }
        : {}),
      ...(selectedTreatment?.lien &&
      canSetMedicalTreatmentBillLien(billInitialValues?.recordStatus)
        ? {
            lien: selectedTreatment.lien,
          }
        : {}),
      ...normalizeBillFormValuesForStatus(billInitialValues ?? {}),
      recordRequestDate: dayjs().format("YYYY-MM-DD"),
      recordStatus:
        getSelectableTreatmentRecordStatus(billInitialValues?.recordStatus) ||
        "Requested",
      recordLabel: billInitialValues?.recordLabel ?? "",
      medicalTreatmentAppointmentId:
        appointmentsWIthSelectedBill?.map((appointment) => appointment.id) ??
        [],
    }),
    [
      billInitialValues,
      appointmentsWIthSelectedBill,
      selectedTreatment?.letterOfProtection,
      selectedTreatment?.lien,
    ],
  );

  const billForm = useForm<z.infer<typeof billFormSchema>>({
    resolver: zodResolver(billFormSchema),
    values:
      editing && (billInitialValues || appointmentsWIthSelectedBill?.length)
        ? editFormValues
        : newBillFormValues,
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });

  const isFormActuallyDirty =
    Object.keys(billForm.formState.dirtyFields).length > 0;

  const [closeAfterSave, setCloseAfterSave] = useState(false);

  const onInvalid: SubmitErrorHandler<z.infer<typeof billFormSchema>> = (
    errors,
  ) => {
    console.error("Treatment bill form validation failed", {
      editing,
      billId: selectedBill?.id,
      errors,
      values: billForm.getValues(),
    });
  };

  const onSubmit = async (data: z.infer<typeof billFormSchema>) => {
    let resData;
    const contactConnectionPromises: Promise<APIResponse<unknown>>[] = [];
    const { medicalTreatmentAppointmentId: submittedAppointmentIds, ...rest } =
      data;
    const normalizedBillFields =
      data.recordStatus === "Received"
        ? {}
        : {
            ...receivedOnlyBillFields,
            recordReceivedDate: null,
          };
    const normalizedLienField = canSetMedicalTreatmentBillLien(
      data.recordStatus,
    )
      ? {}
      : { lien: "" as const };
    const existingAppointmentIds = appointmentsWIthSelectedBill?.map(
      (appointment) => appointment.id,
    );
    const appointmentsToAdd = difference(
      submittedAppointmentIds,
      existingAppointmentIds ?? [],
    );
    const appointmentsToRemove = difference(
      existingAppointmentIds,
      submittedAppointmentIds ?? [],
    );
    if (
      (appointmentsToAdd.length > 0 && !canCreateAppointmentConnections) ||
      (appointmentsToRemove.length > 0 && !canDeleteAppointmentConnections)
    ) {
      return;
    }
    if (editing && selectedBill) {
      try {
        const result = await updateBill.mutateAsync({
          matterId: matter.id,
          id: selectedBill.id,
          dateModified: selectedBill.dateModified,
          ...rest,
          ...normalizedBillFields,
          ...normalizedLienField,
          recordRequestDate: ["Requested", "Received"].includes(
            data.recordStatus,
          )
            ? data.recordRequestDate
            : null,
          recordReceivedDate:
            data.recordStatus === "Received" ? data.recordReceivedDate : null,
          recordRejectedDate:
            data.recordStatus === "Rejected" ? data.recordRejectedDate : null,
          recordRejectedReason:
            data.recordStatus === "Rejected" ? data.recordRejectedReason : null,
        });
        resData = result;
        for (const appointmentId of appointmentsToAdd) {
          contactConnectionPromises.push(
            createMedicalTreatmentAppointmentConnection.mutateAsync({
              matterId: matter.id,
              medicalTreatmentBillId: selectedBill.id,
              medicalTreatmentAppointmentId: appointmentId,
            }),
          );
        }
        for (const appointmentId of appointmentsToRemove) {
          contactConnectionPromises.push(
            deleteMedicalTreatmentAppointmentConnection.mutateAsync({
              matterId: matter.id,
              id:
                matter.medicalTreatmentAppointmentConnections.find(
                  (r) =>
                    r.medicalTreatmentAppointmentId === appointmentId &&
                    r.medicalTreatmentBillId === selectedBill.id,
                )?.id ?? "",
            }),
          );
        }
        await Promise.all(contactConnectionPromises);

        if (closeAfterSave) {
          setOpen(false);
        }
        await invalidateMatterAndSearchQueries(queryClient);
        toast.success("Medical treatment bill updated successfully");
      } catch (error) {
        console.error(error);
      }
    } else if (selectedTreatment) {
      try {
        const result = await createBill.mutateAsync({
          matterId: matter.id,
          medicalTreatmentId: selectedTreatment.id,
          ...rest,
          ...normalizedBillFields,
          ...normalizedLienField,
        });
        resData = result;
        if (afterCreateFn) {
          afterCreateFn(result);
        }
        for (const appointmentId of appointmentsToAdd) {
          contactConnectionPromises.push(
            createMedicalTreatmentAppointmentConnection.mutateAsync({
              matterId: matter.id,
              medicalTreatmentBillId: result.data.id,
              medicalTreatmentAppointmentId: appointmentId,
            }),
          );
        }
        await Promise.all(contactConnectionPromises);
        updateSlideoverPayload("treatment-bill", {
          title: SLIDEOVER_TITLE.TREATMENT_BILL.UPDATE,
          description: SLIDEOVER_DESCRIPTION.TREATMENT_BILL.UPDATE,
          editing: true,
          billId: resData.data.id,
          treatmentId: selectedTreatment.id,
        });
        if (closeAfterSave) {
          setOpen(false);
        }
        await invalidateMatterAndSearchQueries(queryClient);
        toast.success("Medical treatment bill created successfully");
      } catch (error) {
        console.error(error);
      }
    }

    if (!closeAfterSave && resData) {
      const sanitizedData = omitBy(resData.data, isNil) as MedicalTreatmentBill;
      billForm.reset({
        ...defaultFormValues,
        ...sanitizedData,
        recordStatus: getSelectableTreatmentRecordStatus(
          sanitizedData.recordStatus,
        ),
        medicalTreatmentAppointmentId: appointmentsWIthSelectedBill?.map(
          (appointment) => appointment.id,
        ),
      });
    }
  };

  useEffect(() => {
    if (!open) {
      billForm.reset(editing ? editFormValues : newBillFormValues);
    }
  }, [open, billForm, editing, editFormValues, newBillFormValues]);

  const billStatusFormValue = billForm.watch("recordStatus");
  const canSetBillLien = canSetMedicalTreatmentBillLien(billStatusFormValue);
  const originalBillFormValue = billForm.watch("originalBill");

  // Handler for status change
  const handleStatusChange = (newStatus: string) => {
    const currentRequestDate = billForm.getValues("recordRequestDate");
    const currentReceivedDate = billForm.getValues("recordReceivedDate");
    const currentRejectedDate = billForm.getValues("recordRejectedDate");

    // When status changes to "Requested" or "Received", set request date to today if empty
    if (["Requested", "Received"].includes(newStatus) && !currentRequestDate) {
      billForm.setValue("recordRequestDate", dayjs().format("YYYY-MM-DD"), {
        shouldDirty: true,
      });
    }

    // When status changes to "Received", set received date to today if empty
    if (newStatus === "Received" && !currentReceivedDate) {
      billForm.setValue("recordReceivedDate", dayjs().format("YYYY-MM-DD"), {
        shouldDirty: true,
      });
    }

    // When status changes to "Rejected", set rejected date to today if empty
    if (newStatus === "Rejected" && !currentRejectedDate) {
      billForm.setValue("recordRejectedDate", dayjs().format("YYYY-MM-DD"), {
        shouldDirty: true,
      });
    }

    if (newStatus !== "Received") {
      for (const [fieldName, value] of Object.entries(receivedOnlyBillFields)) {
        billForm.setValue(
          fieldName as keyof typeof receivedOnlyBillFields,
          value,
          { shouldDirty: true },
        );
      }

      billForm.setValue("recordReceivedDate", "", {
        shouldDirty: true,
      });
    }

    if (!canSetMedicalTreatmentBillLien(newStatus)) {
      billForm.setValue("lien", undefined, {
        shouldDirty: true,
      });
    }
  };
  const adjustedBillFormValue = billForm.watch("adjustedBill");
  const negotiatedAmountFormValue = billForm.watch("negotiatedAmount");

  const clientPaidFormValue = billForm.watch("clientPaid");
  const insurancePaidFormValue = billForm.watch("insurancePaid");
  const medpayPipPaidFormValue = billForm.watch("medpayPipPaid");

  const { totalBill, totalPaid, totalOutstanding } =
    calculateMedicalTreatmentBillTotals([
      {
        negotiatedAmount: negotiatedAmountFormValue,
        adjustedBill: adjustedBillFormValue,
        originalBill: originalBillFormValue,
        clientPaid: clientPaidFormValue,
        insurancePaid: insurancePaidFormValue,
        medpayPipPaid: medpayPipPaidFormValue,
      },
    ]);
  const billFormId = `treatment-bill-form-${billId ?? treatmentId ?? "new"}`;

  return (
    <>
      <Form {...billForm}>
        <AppModal
          open={open}
          onOpenChange={setOpen}
          title={title}
          description={description}
          isDirty={isFormActuallyDirty}
          loading={isFormLoading}
          size="3xl"
          bodyContained
          footer={
            <div className="flex w-full flex-wrap items-center justify-between gap-2">
              <div className="min-w-0">
                {selectedBill &&
                  hasActionPermission(
                    ACTION_PERMISSION_KEYS.matterMedicalTreatmentBill,
                    ACTION_PERMISSION_VALUES.delete,
                  ) && (
                    <Button
                      type="button"
                      variant="destructive"
                      onClick={() => {
                        setIsDeleting(true);
                      }}
                    >
                      Delete
                    </Button>
                  )}
              </div>
              <div className="flex flex-wrap justify-end gap-2">
                <FormCancelButton
                  setOpen={setOpen}
                  isDirty={isFormActuallyDirty}
                />
                <FormSubmitButton
                  submitFormId={billFormId}
                  variant="light-primary"
                  loading={isFormLoading}
                  overrideDisabled={Boolean(initialData)}
                  submitFn={() => {
                    setCloseAfterSave(false);
                  }}
                />
                <FormSubmitButton
                  submitFormId={billFormId}
                  variant="default"
                  loading={isFormLoading}
                  overrideDisabled={Boolean(initialData)}
                  submitFn={() => {
                    setCloseAfterSave(true);
                  }}
                  title="Save and close"
                />
              </div>
            </div>
          }
        >
          <form
            id={billFormId}
            className="relative flex min-h-0 flex-1 flex-col overflow-hidden"
            onSubmit={async (e) => {
              e.preventDefault();
              e.stopPropagation();
              await billForm.handleSubmit(onSubmit, onInvalid)(e);
            }}
          >
            {isFormLoading && <FormLoadingOverlay />}
            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
              <fieldset
                className={modalFormFieldsetClassName}
                disabled={
                  editing
                    ? !hasActionPermission(
                        ACTION_PERMISSION_KEYS.matterMedicalTreatmentBill,
                        ACTION_PERMISSION_VALUES.update,
                      )
                    : false
                }
              >
                {billStatusFormValue === "Received" && (
                  <TextFormField
                    register={billForm.register("recordLabel")}
                    label="Record name"
                    maxLength={100}
                  />
                )}
                <SimpleSelectFormField
                  register={billForm.register("recordStatus")}
                  formatItemLabel={(item) => item}
                  label="Record status*"
                  items={MEDICAL_TREATMENT_RECORD_SELECTABLE_STATUS_OPTIONS}
                  placeholder="Select status"
                  onChange={(value) => {
                    if (!value) return;
                    handleStatusChange(value);
                  }}
                />
                <div className="2 col-span-full grid grid-cols-subgrid">
                  {["Requested", "Received"].includes(billStatusFormValue) && (
                    <DateFormField
                      name="recordRequestDate"
                      register={billForm.register("recordRequestDate")}
                      label="Record request date*"
                    />
                  )}
                  {billStatusFormValue === "Received" && (
                    <DateFormField
                      name="recordReceivedDate"
                      register={billForm.register("recordReceivedDate")}
                      label="Record received date*"
                    />
                  )}
                  {billStatusFormValue === "Rejected" && (
                    <DateFormField
                      name="recordRejectedDate"
                      register={billForm.register("recordRejectedDate")}
                      label="Record rejected date*"
                    />
                  )}
                </div>
                {billStatusFormValue === "Rejected" && (
                  <TextAreaFormField
                    register={billForm.register("recordRejectedReason")}
                    label="Rejected reason*"
                    className="col-span-full"
                  />
                )}
                {billStatusFormValue === "Received" && (
                  <>
                    <div className="col-span-full flex items-baseline gap-x-2">
                      <Typography
                        variant="heading"
                        className="flex items-center gap-x-2"
                      >
                        Bill
                      </Typography>
                      <Typography
                        className="text-muted-foreground flex"
                        size="sm"
                      >
                        {Number(totalBill) > 0 && (
                          <>
                            (<CurrencyWrapper value={totalBill} />)
                          </>
                        )}
                      </Typography>
                    </div>
                    <CurrencyFormField
                      register={billForm.register("originalBill")}
                      label="Original bill"
                    />
                    <CurrencyFormField
                      register={billForm.register("goalAmount")}
                      label="Goal amount"
                    />
                    <CurrencyFormField
                      register={billForm.register("adjustedBill")}
                      label="Adjusted bill"
                    />
                    <CurrencyFormField
                      register={billForm.register("negotiatedAmount")}
                      label="Negotiated amount"
                    />
                    <div className="col-span-full flex items-baseline gap-x-2">
                      <Typography variant="heading">Payments</Typography>
                      <Typography
                        className="text-muted-foreground flex"
                        size="sm"
                      >
                        {Number(totalPaid) > 0 && (
                          <>
                            (<CurrencyWrapper value={totalPaid} />)
                          </>
                        )}
                      </Typography>
                    </div>
                    <CurrencyFormField
                      register={billForm.register("clientPaid")}
                      label="Client paid"
                    />
                    <CurrencyFormField
                      register={billForm.register("insurancePaid")}
                      label="Insurance paid"
                    />
                    <CurrencyFormField
                      register={billForm.register("medpayPipPaid")}
                      label="Medpay/PIP paid"
                    />
                    <div className="col-span-full flex items-baseline gap-x-2">
                      <Typography variant="subHeading" className="mt-4">
                        Outstanding balance
                        <CurrencyWrapper value={totalOutstanding} />
                      </Typography>
                    </div>
                  </>
                )}
                <div className="col-span-full">
                  <TextAreaFormField
                    register={billForm.register("description")}
                    label="Description"
                  />
                </div>
                <TextFormField
                  register={billForm.register("fileNumber")}
                  label="File number"
                  type="number"
                />
                <div className="col-span-full grid grid-cols-1 gap-4 md:grid-cols-2">
                  <SimpleSelectFormField
                    items={
                      MEDICAL_TREATMENT_LETTER_OF_PROTECTION_STATUS_OPTIONS
                    }
                    label="Letter of protection"
                    placeholder="Select status"
                    clearable
                    register={billForm.register("letterOfProtection")}
                  />
                  {canSetBillLien && (
                    <SimpleSelectFormField
                      items={MEDICAL_TREATMENT_LIEN_STATUS_OPTIONS}
                      label="Lien"
                      placeholder="Select status"
                      clearable
                      register={billForm.register("lien")}
                    />
                  )}
                </div>
                <CheckboxFormField
                  register={billForm.register("reductionFeeAgreement")}
                  label="Reduction fee agreement"
                />
                {selectedTreatment && !creatingFromAppointment && (
                  <fieldset
                    className="contents"
                    disabled={
                      !canCreateAppointmentConnections ||
                      (editing && !canDeleteAppointmentConnections)
                    }
                  >
                    <SelectTreatmentAppointmentsFormField
                      register={billForm.register(
                        "medicalTreatmentAppointmentId",
                      )}
                      label="Associated treatment dates"
                      matter={matter}
                      treatment={selectedTreatment}
                    />
                  </fieldset>
                )}
                <div className="col-span-full mt-6 grid grid-cols-subgrid gap-y-2">
                  <div className="col-span-full">
                    <Separator />
                  </div>
                  <FileReferencesSection
                    editing={editing}
                    fileReferences={fileReferences}
                    matter={matter}
                    itemKey="medicalTreatmentBillId"
                    itemId={billInitialValues?.id ?? ""}
                    permissionKey={
                      ACTION_PERMISSION_KEYS.matterMedicalTreatmentBill
                    }
                    maxFiles={1}
                  />
                </div>
              </fieldset>
              {selectedBill && selectedBill.trustChecks.length > 0 && (
                <div className="bg-light-primary/50 m-6 flex flex-col gap-y-2 rounded-md p-2">
                  <Typography variant="heading">Trust checks</Typography>
                  <div className="flex flex-col gap-y-2">
                    {selectedBill.trustChecks.map((trustCheck) => (
                      <TrustCheckCard
                        key={trustCheck.id}
                        trustCheck={trustCheck}
                        matter={matter}
                        className="border-border border"
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </form>
        </AppModal>
      </Form>
      <ActionConfirmationModal
        open={isDeleting}
        setOpen={setIsDeleting}
        title="Delete bill"
        description="Are you sure you want to delete this bill? This action cannot be undone."
        buttonText="Delete"
        afterActionFn={() => {
          if (selectedBill) {
            deleteBill.mutate({
              matterId: matter.id,
              id: selectedBill.id,
            });
            setOpen(false);
          }
        }}
      />
    </>
  );
};
